import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom'; // Added useLocation for state handling
import { useAuth } from '../context/AuthContext'; // Already imported for user profile logic

import { apiGet, apiDelete } from '../config/apiClient';
import NotificationSettings from '../components/NotificationSettings';

export default function ProfilePage() {
  const [user, setUser] = useState(null);
  const [uploadedReceipts, setUploadedReceipts] = useState([]);
  const [receiptsLoading, setReceiptsLoading] = useState(true);
  const [receiptsError, setReceiptsError] = useState('');
  const [receiptsPagination, setReceiptsPagination] = useState(null);
  const [currentReceiptPage, setCurrentReceiptPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [savedProducts, setSavedProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(true);
  const [productsError, setProductsError] = useState('');
  const [productsPagination, setProductsPagination] = useState(null);
  const [currentProductPage, setCurrentProductPage] = useState(1);
  const [selectedReceiptDetails, setSelectedReceiptDetails] = useState(null);
  const [loadingReceiptId, setLoadingReceiptId] = useState(null);
  const [showUploadSuccess, setShowUploadSuccess] = useState(false);
  const [actionMessage, setActionMessage] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { authToken, user: authUser, points: authPoints, isAuthenticated, fetchUserDetailsAndPoints } = useAuth();

  // Handle newly uploaded receipt highlighting
  useEffect(() => {
    if (location.state?.newReceiptId && location.state?.uploadMessage) {
      setShowUploadSuccess(location.state.uploadMessage);
      // Clear the state after showing
      setTimeout(() => {
        setShowUploadSuccess(false);
        // Clear the navigation state
        navigate('/profile', { replace: true, state: {} });
      }, 5000);
    }
  }, [location.state, navigate]);

  useEffect(() => {
    if (isAuthenticated && !authUser) {
        setLoading(true);
        fetchUserDetailsAndPoints(authToken).finally(() => setLoading(false));
    } else if (authUser) {
        setUser(authUser);
        setLoading(false);
    } else if (!isAuthenticated) {
        setError('You are not logged in. Please log in to view your profile.');
        setLoading(false);
    }
  }, [isAuthenticated, authUser, authToken, fetchUserDetailsAndPoints]);

  const fetchUserReceipts = async (page = 1) => {
    if (!user) { 
      setReceiptsLoading(false);
      return;
    }

    setReceiptsLoading(true);
    setReceiptsError('');
    try {
      const data = await apiGet(`/receipts/my-receipts-centralized?page=${page}`);
      setUploadedReceipts(data.data || []);
      setReceiptsPagination({ current_page: data.current_page, last_page: data.last_page, from: data.from, to: data.to, total: data.total });
    } catch (err) {
      console.error('Fetch receipts error:', err);
      setReceiptsError(err.data?.message || err.message || 'An error occurred while fetching your receipts.');
    }
    setReceiptsLoading(false);
  };

  useEffect(() => {
    if (user) {
        fetchUserReceipts(currentReceiptPage);
    }
  }, [user, currentReceiptPage, authToken]);

  // Function to fetch detailed receipt information
  const fetchReceiptDetails = async (receiptId) => {
    setLoadingReceiptId(receiptId);
    try {
      const data = await apiGet(`/receipts/${receiptId}/status-centralized`);
      setSelectedReceiptDetails(data.data);
    } catch (err) {
      console.error('Fetch receipt details error:', err);
    }
    setLoadingReceiptId(null);
  };

  // Enhanced status display function
  const getStatusDisplay = (status) => {
    const statusConfig = {
      'uploaded': { 
        text: 'Uploaded', 
        color: 'bg-indigo-100 text-indigo-700',
        description: 'Receipt uploaded successfully'
      },
      'pending_ocr': { 
        text: 'Queued', 
        color: 'bg-indigo-100 text-indigo-700',
        description: 'Waiting for processing to start'
      },
      'processing': { 
        text: 'Processing', 
        color: 'bg-yellow-100 text-yellow-700',
        description: 'Extracting data from receipt...'
      },
      'processed_ocr': { 
        text: 'Extracting Items', 
        color: 'bg-yellow-100 text-yellow-700',
        description: 'OCR completed, processing items...'
      },
      'processing_items': { 
        text: 'Processing Items', 
        color: 'bg-yellow-100 text-yellow-700',
        description: 'Matching products and prices...'
      },
      'pending_vendor_approval': { 
        text: 'Pending Vendor Approval', 
        color: 'bg-orange-100 text-orange-700',
        description: 'Vendor needs approval before processing can continue'
      },
      'pending_user_action': { 
        text: 'Photos Needed', 
        color: 'bg-purple-100 text-purple-700',
        description: 'Some items need photos for verification'
      },
      'pending_admin_action': { 
        text: 'Under Review', 
        color: 'bg-orange-100 text-orange-700',
        description: 'Admin is reviewing vendor or items'
      },
      'completed': { 
        text: 'Processed', 
        color: 'bg-green-100 text-green-700',
        description: 'Receipt processed successfully'
      },
      'completed_no_items': { 
        text: 'No Items Found', 
        color: 'bg-orange-100 text-orange-700',
        description: 'Receipt processed but no items detected'
      },
      'pending_manual_review': { 
        text: 'Needs Review', 
        color: 'bg-orange-100 text-orange-700',
        description: 'Requires manual review'
      },
      'processing_failed': { 
        text: 'Failed', 
        color: 'bg-red-100 text-red-700',
        description: 'Processing failed, please try again'
      },
      'failed': { 
        text: 'Error', 
        color: 'bg-red-100 text-red-700',
        description: 'An error occurred during processing'
      },
      'failed_no_items': { 
        text: 'Failed - No Items', 
        color: 'bg-red-100 text-red-700',
        description: 'Could not extract any items from receipt'
      }
    };
    return statusConfig[status] || { 
      text: status, 
      color: 'bg-gray-100 text-gray-700',
      description: 'Status unknown'
    };
  };

  // Check if receipt is newly uploaded
  const isNewlyUploaded = (receiptId) => {
    return location.state?.newReceiptId === receiptId;
  };

  const fetchSavedProducts = async (page = 1) => {
    if (!user) { 
      setProductsLoading(false);
      return;
    }

    setProductsLoading(true);
    setProductsError('');
    try {
      const data = await apiGet(`/user/saved-products?page=${page}`);
      setSavedProducts(data.data || []);
      setProductsPagination({ current_page: data.current_page, last_page: data.last_page, from: data.from, to: data.to, total: data.total });
    } catch (err) {
      console.error('Fetch saved products error:', err);
      setProductsError(err.data?.message || err.message || 'An error occurred while fetching your saved products.');
    }
    setProductsLoading(false);
  };

  useEffect(() => {
    if (user) {
        fetchSavedProducts(currentProductPage);
    }
  }, [user, currentProductPage, authToken]);

  const handleUnsaveProduct = async (productId) => {
    if (!authToken) {
      setActionMessage({ type: 'error', text: 'Authentication token not found. Please log in.' });
      return;
    }
    setActionMessage(null);
    try {
      const data = await apiDelete(`/products/${productId}/unsave`);
      setActionMessage({ type: 'success', text: data?.message || 'Product unsaved successfully.' });
      setSavedProducts(prevProducts => prevProducts.filter(p => p.id !== productId));
      fetchSavedProducts(currentProductPage);
    } catch (err) {
      console.error('Unsave error:', err);
      setActionMessage({ type: 'error', text: err.data?.message || err.message || 'An error occurred while unsaving the product.' });
    }
  };

  const handleReceiptPageChange = (newPage) => {
      if (newPage >= 1 && receiptsPagination && newPage <= receiptsPagination.last_page) setCurrentReceiptPage(newPage);
  };

  const handleProductPageChange = (newPage) => {
      if (newPage >= 1 && productsPagination && newPage <= productsPagination.last_page) setCurrentProductPage(newPage);
  };

  // Refresh receipts function
  const refreshReceipts = () => {
    fetchUserReceipts(currentReceiptPage);
  };

  if (loading || (isAuthenticated && !authUser)) {
    return <div className="text-center py-20">Loading profile...</div>;
  }

  if (!isAuthenticated || !authUser) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-12 text-center">
        <h1 className="text-2xl font-bold mb-6 text-gray-800">My Profile</h1>
        <div className="bg-red-100 text-red-700 p-4 rounded-lg shadow">{error || 'Please log in to view your profile.'}</div>
        <button onClick={() => navigate('/login')} className="mt-4 bg-red-800 hover:bg-red-900 text-white font-semibold px-6 py-2 rounded-lg shadow">Go to Login</button>
      </div>
    );
  }

  const displayUser = authUser || user;
  const displayPoints = authPoints !== null ? authPoints : 0;

  return (
    <div className="max-w-4xl mx-auto px-4 py-12">
      <h1 className="text-2xl font-bold mb-6 text-gray-800">My Profile</h1>

      {/* Upload Success Message */}
      {showUploadSuccess && (
        <div className="mb-6 bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded-lg">
          <div className="flex items-center">
            <span className="text-xl mr-2">🎉</span>
            <div>
              <strong>Upload Successful!</strong>
              <p className="text-sm">{showUploadSuccess}</p>
            </div>
          </div>
        </div>
      )}

      {actionMessage && (
        <div className={`mb-6 px-4 py-3 rounded-lg flex items-center justify-between text-sm ${
          actionMessage.type === 'success' 
            ? 'bg-green-50 border border-green-300 text-green-700' 
            : 'bg-red-50 border border-red-300 text-red-700'
        }`}>
          <span>{actionMessage.text}</span>
          <button onClick={() => setActionMessage(null)} className="ml-4 font-bold hover:opacity-70">&times;</button>
        </div>
      )}

      {/* User Info Card */}
      <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
        <div className="flex items-center gap-6 mb-4">
            <div className="w-16 h-16 bg-gradient-to-r from-red-700 to-red-900 rounded-full flex items-center justify-center text-white text-xl font-bold">
                {displayUser?.name?.charAt(0)?.toUpperCase() || '?'}
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-800">{displayUser?.name || 'N/A'}</h2>
              <div className="text-gray-600 text-md">{displayUser?.email || 'N/A'}</div>
              {displayUser?.phone && <div className="text-gray-500 text-sm">Phone: {displayUser?.phone}</div>}
            </div>
        </div>
        <div className="border-t pt-4">
            <h3 className="text-lg font-semibold text-gray-700">Community Points</h3>
            <p className="text-2xl font-bold text-yellow-500">{displayPoints} pts</p>
            <p className="text-xs text-gray-500 mt-1">Share receipts to earn more searches.</p>
            {displayPoints <= 2 && (
              <div className={`mt-3 p-3 rounded-lg ${displayPoints <= 0 ? 'bg-red-50 border border-red-200' : 'bg-orange-50 border border-orange-200'}`}>
                <p className={`text-sm font-medium ${displayPoints <= 0 ? 'text-red-700' : 'text-orange-700'}`}>
                  {displayPoints <= 0 
                    ? 'You\'re out of points! Upload a receipt to keep searching.' 
                    : 'Points running low. Upload a receipt to stock up.'}
                </p>
                <button 
                  onClick={() => navigate('/upload')} 
                  className="mt-2 text-sm bg-red-800 text-white px-4 py-1.5 rounded-lg hover:bg-red-900 transition font-medium"
                >
                  Upload Receipt
                </button>
              </div>
            )}
        </div>
      </div>

      {/* Receipts Section */}
      <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold text-gray-700">My Uploaded Receipts</h2>
          <button 
            onClick={refreshReceipts}
            className="text-red-800 hover:text-red-900 text-sm flex items-center gap-1"
            disabled={receiptsLoading}
          >
            <span className={receiptsLoading ? "animate-spin" : ""}>🔄</span>
            Refresh
          </button>
        </div>
        
        {receiptsLoading && (
          <div className="text-center py-5">
            <div className="animate-spin h-6 w-6 border-b-2 border-red-800 mx-auto"></div>
            <p className="text-sm text-gray-500 mt-2">Loading receipts...</p>
          </div>
        )}
        
        {receiptsError && (
          <div className="text-center py-5 bg-red-50 text-red-600 p-3 rounded">
            <p>Error: {receiptsError}</p>
          </div>
        )}
        
        {!receiptsLoading && !receiptsError && uploadedReceipts.length === 0 && (
          <div className="text-center py-8 bg-gray-50 p-6 rounded-lg">
            <p className="text-gray-500 mb-4">You haven't uploaded any receipts yet.</p>
            <button 
              onClick={() => navigate('/upload')}
              className="bg-red-800 text-white px-4 py-2 rounded-lg hover:bg-red-900 transition"
            >
              Upload Your First Receipt
            </button>
          </div>
        )}
        
        {!receiptsLoading && !receiptsError && uploadedReceipts.length > 0 && (
          <>
            <div className="space-y-3">
              {uploadedReceipts.map(receipt => {
                const statusDisplay = getStatusDisplay(receipt.status);
                const isNew = isNewlyUploaded(receipt.id);
                
                return (
                  <div 
                    key={receipt.id} 
                    className={`border rounded-lg p-4 transition-all duration-300 ${
                      isNew 
                        ? 'border-green-400 bg-green-50 shadow-md' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          {isNew && <span className="text-green-600 text-sm font-semibold">✨ NEW</span>}
                          <h3 className="font-medium text-gray-800">
                            {receipt.original_filename || 'Receipt'}
                          </h3>
                        </div>
                        
                        <div className="text-sm text-gray-500 mb-2">
                          Uploaded: {new Date(receipt.created_at).toLocaleDateString()} at {new Date(receipt.created_at).toLocaleTimeString()}
                        </div>
                        
                        <div className="flex items-center gap-2 mb-2">
                          <span className={`text-xs px-2 py-1 rounded-full ${statusDisplay.color}`}>
                            {statusDisplay.text}
                          </span>
                          <span className="text-xs text-gray-500">{statusDisplay.description}</span>
                        </div>
                        
                        {receipt.vendor_name && (
                          <div className="text-sm text-gray-600">
                            Store: <span className="font-medium">{receipt.vendor_name}</span>
                          </div>
                        )}
                        
                        {receipt.total_amount && (
                          <div className="text-sm text-gray-600">
                            Total: <span className="font-medium">${receipt.total_amount}</span>
                          </div>
                        )}
                      </div>
                      
                      <div className="flex flex-col gap-2">
                        <button
                          onClick={() => fetchReceiptDetails(receipt.id)}
                          className="bg-red-800 text-white text-sm px-3 py-1 rounded hover:bg-red-900 transition"
                          disabled={loadingReceiptId === receipt.id}
                        >
                          {loadingReceiptId === receipt.id ? 'Loading...' : 'View Details'}
                        </button>
                        
                        {(receipt.status === 'processing' || receipt.status === 'uploaded') && (
                          <button
                            onClick={refreshReceipts}
                            className="bg-gray-600 text-white text-sm px-3 py-1 rounded hover:bg-gray-700 transition"
                          >
                            Check Status
                          </button>
                        )}
                      </div>
                    </div>
                    
                    {/* Receipt Details Panel */}
                    {selectedReceiptDetails && selectedReceiptDetails.id === receipt.id && (
                      <div className="mt-4 pt-4 border-t border-gray-200">
                        <h4 className="font-medium text-gray-800 mb-2">Receipt Details</h4>
                        
                        {selectedReceiptDetails.error_message && (
                          <div className="bg-red-50 text-red-700 p-3 rounded mb-3">
                            <strong>Error:</strong> {selectedReceiptDetails.error_message}
                          </div>
                        )}
                        
                        {selectedReceiptDetails.processed_items && selectedReceiptDetails.processed_items.length > 0 && (
                          <div className="bg-gray-50 p-3 rounded">
                            <h5 className="font-medium mb-2">Processed Items ({selectedReceiptDetails.items_count})</h5>
                            <div className="space-y-1 max-h-32 overflow-y-auto">
                              {selectedReceiptDetails.processed_items.slice(0, 5).map((item, index) => (
                                <div key={index} className="text-sm flex justify-between">
                                  <span>{item.item?.Item_name || 'Unknown item'}</span>
                                  <span className="font-medium">${item.item?.Item_unit_price || '0.00'}</span>
                                </div>
                              ))}
                              {selectedReceiptDetails.processed_items.length > 5 && (
                                <div className="text-xs text-gray-500">
                                  ...and {selectedReceiptDetails.processed_items.length - 5} more items
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                        
                        <button
                          onClick={() => setSelectedReceiptDetails(null)}
                          className="mt-2 text-gray-600 text-sm hover:text-gray-800"
                        >
                          Hide Details
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            
            {receiptsPagination && receiptsPagination.last_page > 1 && (
              <div className="flex flex-col sm:flex-row justify-between items-center gap-4 pt-4 border-t border-gray-100 mt-6">
                <div className="text-xs text-gray-500">
                  Showing {receiptsPagination.from || 0}-{receiptsPagination.to || 0} of {receiptsPagination.total || 0}
                </div>
                <div className="flex items-center gap-1.5">
                  <button onClick={() => handleReceiptPageChange(1)} disabled={currentReceiptPage === 1} className="px-2 py-1 text-xs border rounded disabled:opacity-50">First</button>
                  <button onClick={() => handleReceiptPageChange(currentReceiptPage - 1)} disabled={currentReceiptPage === 1} className="px-2 py-1 text-xs border rounded disabled:opacity-50">&laquo;</button>
                  <span className="text-xs text-gray-600">Pg {currentReceiptPage} of {receiptsPagination.last_page}</span>
                  <button onClick={() => handleReceiptPageChange(currentReceiptPage + 1)} disabled={currentReceiptPage === receiptsPagination.last_page} className="px-2 py-1 text-xs border rounded disabled:opacity-50">&raquo;</button>
                  <button onClick={() => handleReceiptPageChange(receiptsPagination.last_page)} disabled={currentReceiptPage === receiptsPagination.last_page} className="px-2 py-1 text-xs border rounded disabled:opacity-50">Last</button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Notification Settings */}
      <NotificationSettings />

      {/* Saved Products Section */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <h2 className="text-xl font-semibold mb-4 text-gray-700">My Saved Products</h2>
        {productsLoading && ( <div className="text-center py-5"><div className="animate-spin h-6 w-6 border-b-2 border-red-800 mx-auto"></div><p className="text-sm text-gray-500 mt-2">Loading saved products...</p></div> )}
        {productsError && ( <div className="text-center py-5 bg-red-50 text-red-600 p-3 rounded"><p>Error: {productsError}</p></div> )}
        {!productsLoading && !productsError && savedProducts.length === 0 && ( <div className="text-center py-5 bg-gray-50 p-3 rounded"><p className="text-gray-500">You haven't saved any products yet.</p></div> )}
        {!productsLoading && !productsError && savedProducts.length > 0 && (
          <>
            <ul className="space-y-3 mb-6">
              {savedProducts.map(product => (
                <li key={product.id} className="flex justify-between items-center p-3 border rounded-lg hover:bg-gray-50">
                  <div>
                                                <Link to={`/product/${product.id}`} className="text-red-800 hover:text-red-900 font-medium">
                      {product.name}
                    </Link>
                    {product.brand?.name && <span className="text-xs text-gray-500 ml-2">by {product.brand.name}</span>}
                    {product.category?.name && <span className="text-xs text-gray-500 ml-2">in {product.category.name}</span>}
                  </div>
                  <button onClick={() => handleUnsaveProduct(product.id)} className="text-red-600 hover:text-red-800 text-sm px-2 py-1 border border-red-300 rounded hover:bg-red-50">
                    Remove
                  </button>
                </li>
              ))}
            </ul>
            {productsPagination && productsPagination.last_page > 1 && (
              <div className="flex flex-col sm:flex-row justify-between items-center gap-4 pt-4 border-t border-gray-100">
                <div className="text-xs text-gray-500">
                  Showing {productsPagination.from || 0}-{productsPagination.to || 0} of {productsPagination.total || 0}
                </div>
                <div className="flex items-center gap-1.5">
                  <button onClick={() => handleProductPageChange(1)} disabled={currentProductPage === 1} className="px-2 py-1 text-xs border rounded disabled:opacity-50">First</button>
                  <button onClick={() => handleProductPageChange(currentProductPage - 1)} disabled={currentProductPage === 1} className="px-2 py-1 text-xs border rounded disabled:opacity-50">&laquo;</button>
                  <span className="text-xs text-gray-600">Pg {currentProductPage} of {productsPagination.last_page}</span>
                  <button onClick={() => handleProductPageChange(currentProductPage + 1)} disabled={currentProductPage === productsPagination.last_page} className="px-2 py-1 text-xs border rounded disabled:opacity-50">&raquo;</button>
                  <button onClick={() => handleProductPageChange(productsPagination.last_page)} disabled={currentProductPage === productsPagination.last_page} className="px-2 py-1 text-xs border rounded disabled:opacity-50">Last</button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
} 